import { useState, useCallback } from 'react';

export interface UseScreenshotCaptureReturn {
  isCapturing: boolean;
  isSupported: boolean;
  captureScreenshot: () => Promise<File | null>;
  error: string | null;
  clearError: () => void;
}

export function useScreenshotCapture(): UseScreenshotCaptureReturn {
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported = !!(
    navigator &&
    navigator.mediaDevices &&
    navigator.mediaDevices.getDisplayMedia
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const captureScreenshot = useCallback(async (): Promise<File | null> => {
    if (!isSupported) {
      setError('Screen Capture API wird von deinem Browser nicht unterstützt');
      return null;
    }

    setIsCapturing(true);
    setError(null);

    let stream: MediaStream | null = null;

    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always' } as MediaTrackConstraints,
        audio: false,
      });

      return await new Promise<File | null>((resolve) => {
        const video = document.createElement('video');
        video.srcObject = stream;
        video.muted = true;

        const cleanup = () => {
          stream?.getTracks().forEach((track) => track.stop());
          video.srcObject = null;
        };

        const timeoutId = setTimeout(() => {
          cleanup();
          setError('Screenshot-Erfassung hat zu lange gedauert');
          resolve(null);
        }, 30000);

        video.addEventListener(
          'loadedmetadata',
          () => {
            void video.play().then(() => {
              // requestAnimationFrame stellt sicher, dass ein vollständiger Frame vorliegt
              requestAnimationFrame(() => {
                try {
                  const canvas = document.createElement('canvas');
                  canvas.width = video.videoWidth || 1920;
                  canvas.height = video.videoHeight || 1080;

                  const ctx = canvas.getContext('2d');
                  if (!ctx) {
                    clearTimeout(timeoutId);
                    cleanup();
                    setError('Canvas konnte nicht erstellt werden');
                    resolve(null);
                    return;
                  }

                  ctx.drawImage(video, 0, 0);
                  clearTimeout(timeoutId);
                  cleanup();

                  canvas.toBlob(
                    (blob) => {
                      if (!blob) {
                        setError('Screenshot konnte nicht erstellt werden');
                        resolve(null);
                        return;
                      }
                      const filename = `screenshot_${Date.now()}.png`;
                      resolve(new File([blob], filename, { type: 'image/png' }));
                    },
                    'image/png',
                    0.95
                  );
                } catch (drawError) {
                  clearTimeout(timeoutId);
                  cleanup();
                  const msg = drawError instanceof Error ? drawError.message : 'Unbekannter Fehler';
                  setError(`Screenshot fehlgeschlagen: ${msg}`);
                  resolve(null);
                }
              });
            }).catch((playError) => {
              clearTimeout(timeoutId);
              cleanup();
              const msg = playError instanceof Error ? playError.message : 'Unbekannter Fehler';
              setError(`Video konnte nicht gestartet werden: ${msg}`);
              resolve(null);
            });
          },
          { once: true }
        );

        video.addEventListener(
          'error',
          () => {
            clearTimeout(timeoutId);
            cleanup();
            setError('Fehler beim Laden des Video-Streams');
            resolve(null);
          },
          { once: true }
        );
      });
    } catch (err) {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      const domErr = err instanceof DOMException ? err : null;
      if (domErr?.name === 'NotAllowedError') {
        setError('Berechtigung für Bildschirmaufnahme abgelehnt');
      } else if (domErr?.name === 'NotFoundError') {
        setError('Keine Bildschirm-Quelle gefunden');
      } else if (domErr?.name === 'AbortError') {
        // Benutzer hat Auswahl abgebrochen — keine Fehlermeldung nötig
      } else {
        const errMessage = err instanceof Error ? err.message : 'Unbekannter Fehler';
        setError(`Screenshot fehlgeschlagen: ${errMessage}`);
      }
      return null;
    } finally {
      setIsCapturing(false);
    }
  }, [isSupported]);

  return {
    isCapturing,
    isSupported,
    captureScreenshot,
    error,
    clearError,
  };
}
